import { getValidAccessToken } from './getValidAccessToken';
import { normalizeEnvKey } from './envKeyUtils';
import { decodeJwtParts } from './debug/jwtDebug';

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/zoomed-in-earth`;
};

const buildAuthHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };

  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 &&
    !jwtAnonDecoded?.decodeError &&
    jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 &&
    !anonDecoded?.decodeError &&
    anonDecoded?.payload;

  const apikeyToSend = jwtAnonIsUsableJwt
    ? jwtAnonRaw
    : anonLooksJwt
      ? anonRaw
      : null;

  const accessToken = await getValidAccessToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (apikeyToSend) headers.apikey = apikeyToSend;

  return headers;
};

const parseJsonResponse = async (response) => {
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  return { data, text };
};

const requestWithAuthRetry = async (body) => {
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const doRequest = async (reqHeaders) =>
    await fetch(getFunctionsUrl(), {
      method: 'POST',
      headers: reqHeaders,
      body: JSON.stringify(body),
    });

  let response = await doRequest(headers);
  if (response.status === 401) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      response = await doRequest(retryHeaders);
    }
  }

  const { data, text } = await parseJsonResponse(response);
  if (!response.ok) {
    const error = new Error(data?.error || text || 'Earth request failed.');
    error.code = data?.code;
    error.status = response.status;
    throw error;
  }

  return data || {};
};

const toFiniteNumber = (value, fallback = null) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const startEarthRound = async ({ mode }) => {
  const data = await requestWithAuthRetry({
    action: 'start',
    mode,
  });

  return {
    roundId: String(data?.roundId || ''),
    mode: String(data?.mode || mode || ''),
    status: String(data?.status || 'active'),
    maxAttempts: toFiniteNumber(data?.maxAttempts, 1),
    attemptsUsed: toFiniteNumber(data?.attemptsUsed, 0),
    revealStep: toFiniteNumber(data?.revealStep, 0),
    zoomLevels: Array.isArray(data?.zoomLevels)
      ? data.zoomLevels.map((v) => toFiniteNumber(v, 100)).filter(Number.isFinite)
      : [],
    imageDataUrl: String(data?.imageDataUrl || ''),
    coins: toFiniteNumber(data?.coins, null),
    cost: toFiniteNumber(data?.cost, null),
    startedAt: data?.startedAt || null,
    region: data?.region || null,
  };
};

export const submitEarthGuess = async ({ roundId, guessLat, guessLng }) => {
  const data = await requestWithAuthRetry({
    action: 'guess',
    roundId,
    guessLat,
    guessLng,
  });

  return {
    roundId: String(data?.roundId || ''),
    mode: String(data?.mode || ''),
    attemptNumber: toFiniteNumber(data?.attemptNumber, 0),
    attemptsUsed: toFiniteNumber(data?.attemptsUsed, 0),
    maxAttempts: toFiniteNumber(data?.maxAttempts, 1),
    remainingAttempts: toFiniteNumber(data?.remainingAttempts, 0),
    distanceKm: toFiniteNumber(data?.distanceKm, null),
    bestDistanceKm: toFiniteNumber(data?.bestDistanceKm, null),
    accuracyPct: toFiniteNumber(data?.accuracyPct, 0),
    isCorrect: Boolean(data?.isCorrect),
    roundFinished: Boolean(data?.roundFinished),
    status: String(data?.status || 'active'),
    revealStep: toFiniteNumber(data?.revealStep, 0),
    zoomLevels: Array.isArray(data?.zoomLevels)
      ? data.zoomLevels.map((v) => toFiniteNumber(v, 100)).filter(Number.isFinite)
      : [],
    answer:
      data?.answer &&
      Number.isFinite(Number(data.answer.lat)) &&
      Number.isFinite(Number(data.answer.lng))
        ? {
            lat: Number(data.answer.lat),
            lng: Number(data.answer.lng),
            country:
              typeof data.answer.country === 'string' && data.answer.country.trim()
                ? data.answer.country.trim()
                : null,
          }
        : null,
  };
};

export const revealEarthRound = async ({ roundId }) => {
  const data = await requestWithAuthRetry({
    action: 'reveal',
    roundId,
  });

  return {
    roundId: String(data?.roundId || ''),
    mode: String(data?.mode || ''),
    status: String(data?.status || 'revealed'),
    attemptsUsed: toFiniteNumber(data?.attemptsUsed, 0),
    maxAttempts: toFiniteNumber(data?.maxAttempts, 1),
    answer:
      data?.answer &&
      Number.isFinite(Number(data.answer.lat)) &&
      Number.isFinite(Number(data.answer.lng))
        ? {
            lat: Number(data.answer.lat),
            lng: Number(data.answer.lng),
            country:
              typeof data.answer.country === 'string' && data.answer.country.trim()
                ? data.answer.country.trim()
                : null,
          }
        : null,
  };
};

export const requestEarthRiddle = async ({ roundId }) => {
  const data = await requestWithAuthRetry({
    action: 'riddle',
    roundId,
  });

  return {
    roundId: String(data?.roundId || ''),
    riddle: typeof data?.riddle === 'string' ? data.riddle.trim() : '',
    cost: toFiniteNumber(data?.cost, null),
    coins: toFiniteNumber(data?.coins, null),
  };
};

export const resumeEarthRound = async () => {
  const data = await requestWithAuthRetry({ action: 'resume' });
  return {
    roundId: String(data?.roundId || ''),
    mode: String(data?.mode || ''),
    status: String(data?.status || 'active'),
    maxAttempts: toFiniteNumber(data?.maxAttempts, 1),
    attemptsUsed: toFiniteNumber(data?.attemptsUsed, 0),
    revealStep: toFiniteNumber(data?.revealStep, 0),
    zoomLevels: Array.isArray(data?.zoomLevels)
      ? data.zoomLevels.map((v) => toFiniteNumber(v, 100)).filter(Number.isFinite)
      : [],
    imageDataUrl: String(data?.imageDataUrl || ''),
    startedAt: data?.startedAt || null,
    guesses: Array.isArray(data?.guesses)
      ? data.guesses.map((g) => ({
          attemptNumber: toFiniteNumber(g?.attemptNumber, 0),
          guessLat: toFiniteNumber(g?.guessLat, null),
          guessLng: toFiniteNumber(g?.guessLng, null),
          distanceKm: toFiniteNumber(g?.distanceKm, null),
          accuracyPct: toFiniteNumber(g?.accuracyPct, 0),
          isCorrect: Boolean(g?.isCorrect),
          createdAt: g?.createdAt || null,
        }))
      : [],
  };
};
